'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useParams, usePathname } from 'next/navigation';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { BackofficeIcon } from './BackofficeIcon';
import { Locale, getLocalePath } from '@/lib/i18n';
import { preloadPageImages } from '@/lib/image-preloader';
import { rafThrottle } from '@/lib/scroll-utils';
import styles from './Header.module.css';

export interface HeaderProps {
  homeLabel?: string;
  homeHref?: string;
  leftNavigation?: Array<{
    label: string;
    href: string;
  }>;
  solutionsLabel?: string;
  solutions?: Array<{
    label: string;
    description: string;
    href: string;
    icon: string;
    dividerBefore?: boolean;
  }>;
  integrationsLabel?: string;
  integrations?: Array<{
    label: string;
    description: string;
    href: string;
    icon: string;
    dividerBefore?: boolean;
  }>;
  viewAllIntegrationsLink?: {
    label: string;
    href: string;
  };
  branchesLabel?: string;
  branches?: Array<{
    label: string;
    description: string;
    href: string;
    icon: string;
  }>;
  viewAllIndustriesLink?: {
    label: string;
    href: string;
  };
  blogsLabel?: string;
  blogs?: Array<{
    label: string;
    description: string;
    href: string;
    icon: string;
  }>;
  viewAllBlogsLink?: {
    label: string;
    href: string;
  };
  reviewsLabel?: string;
  reviewsHref?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

/**
 * Main navigation header with language switcher
 * Sticky on scroll with blur effect
 */
export function Header({ homeLabel, homeHref, leftNavigation, solutionsLabel, solutions, integrationsLabel, integrations, viewAllIntegrationsLink, branchesLabel, branches, viewAllIndustriesLink, reviewsLabel, reviewsHref, blogsLabel, blogs, viewAllBlogsLink, ctaLabel, ctaHref }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isIntegrationsDropdownOpen, setIsIntegrationsDropdownOpen] = useState(false);
  const [isBranchesDropdownOpen, setIsBranchesDropdownOpen] = useState(false);
  const [isBlogsDropdownOpen, setIsBlogsDropdownOpen] = useState(false);
  const [isMobileSolutionsOpen, setIsMobileSolutionsOpen] = useState(false);
  const [isMobileIntegrationsOpen, setIsMobileIntegrationsOpen] = useState(false);
  const [isMobileBranchesOpen, setIsMobileBranchesOpen] = useState(false);
  const [isMobileBlogsOpen, setIsMobileBlogsOpen] = useState(false);
  // Track which dropdown was clicked (vs hovered) to keep it pinned
  const [clickedDropdown, setClickedDropdown] = useState<'solutions' | 'integrations' | 'branches' | 'blogs' | null>(null);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const integrationsDropdownRef = useRef<HTMLLIElement>(null);
  const branchesDropdownRef = useRef<HTMLLIElement>(null);
  const blogsDropdownRef = useRef<HTMLLIElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const params = useParams();
  const pathname = usePathname();
  const locale = (params?.locale as Locale) || 'en';

  useEffect(() => {
    // Check initial scroll position on mount
    setIsScrolled(window.scrollY > 20);

    // Throttle scroll handler with requestAnimationFrame for 60fps performance
    const handleScroll = rafThrottle(() => {
      setIsScrolled(window.scrollY > 20);
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Menu images are now handled by Next.js Image optimization with priority prop
  // No need for manual preloading anymore

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
    setIsIntegrationsDropdownOpen(false);
    setIsBranchesDropdownOpen(false);
    setIsBlogsDropdownOpen(false);
    setIsMobileSolutionsOpen(false);
    setIsMobileIntegrationsOpen(false);
    setIsMobileBranchesOpen(false);
    setIsMobileBlogsOpen(false);
    setClickedDropdown(null);
  }, [pathname]);

  // Lock body scroll when mobile menu is open and restore on close
  useEffect(() => {
    // Simple, flicker-free scroll lock without reposition jumps
    const html = document.documentElement;
    const body = document.body;
    if (isMobileMenuOpen) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'contain';
      body.style.overscrollBehavior = 'contain';
      return () => {
        html.style.overflow = '';
        body.style.overflow = '';
        html.style.overscrollBehavior = '';
        body.style.overscrollBehavior = '';
      };
    } else {
      html.style.overflow = '';
      body.style.overflow = '';
      html.style.overscrollBehavior = '';
      body.style.overscrollBehavior = '';
    }
  }, [isMobileMenuOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside all dropdown containers
      const isOutsideAll = 
        (!dropdownRef.current || !dropdownRef.current.contains(target)) &&
        (!integrationsDropdownRef.current || !integrationsDropdownRef.current.contains(target)) &&
        (!branchesDropdownRef.current || !branchesDropdownRef.current.contains(target)) &&
        (!blogsDropdownRef.current || !blogsDropdownRef.current.contains(target));
      
      if (isOutsideAll) {
        closeAllDropdowns();
        setClickedDropdown(null);
      }
    };

    // Only add listener if at least one dropdown is open
    if (isDropdownOpen || isIntegrationsDropdownOpen || isBranchesDropdownOpen || isBlogsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen, isIntegrationsDropdownOpen, isBranchesDropdownOpen, isBlogsDropdownOpen]);

  // Helper functions for hover behavior
  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const closeAllDropdowns = () => {
    setIsDropdownOpen(false);
    setIsIntegrationsDropdownOpen(false);
    setIsBranchesDropdownOpen(false);
    setIsBlogsDropdownOpen(false);
  };

  const handleDropdownClick = (dropdown: 'solutions' | 'integrations' | 'branches' | 'blogs') => {
    // If clicking the currently open (clicked) dropdown, close it
    if (clickedDropdown === dropdown) {
      closeAllDropdowns();
      setClickedDropdown(null);
      return;
    }
    
    // Otherwise open the clicked dropdown and mark it as clicked
    clearCloseTimeout();
    closeAllDropdowns();
    setClickedDropdown(dropdown);
    
    switch (dropdown) {
      case 'solutions':
        setIsDropdownOpen(true);
        break;
      case 'integrations':
        setIsIntegrationsDropdownOpen(true);
        break;
      case 'branches':
        setIsBranchesDropdownOpen(true);
        break;
      case 'blogs':
        setIsBlogsDropdownOpen(true);
        break;
    }
  };

  const handleMouseEnter = (dropdown: 'solutions' | 'integrations' | 'branches' | 'blogs') => {
    // Don't auto-open on hover if another dropdown is pinned by click
    if (clickedDropdown && clickedDropdown !== dropdown) {
      return;
    }
    
    clearCloseTimeout();
    
    // If this dropdown is not the clicked one, we can hover-open it
    if (!clickedDropdown) {
      closeAllDropdowns();
      
      switch (dropdown) {
        case 'solutions':
          setIsDropdownOpen(true);
          break;
        case 'integrations':
          setIsIntegrationsDropdownOpen(true);
          break;
        case 'branches':
          setIsBranchesDropdownOpen(true);
          break;
        case 'blogs':
          setIsBlogsDropdownOpen(true);
          break;
      }
    }
  };

  const handleMouseLeave = () => {
    // Don't auto-close if a dropdown is pinned by click
    if (clickedDropdown) {
      return;
    }
    
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      closeAllDropdowns();
    }, 200); // 200ms delay before closing
  };

  return (
    <header
      className={`${styles.header} ${isScrolled ? styles.scrolled : ''} ${isMobileMenuOpen ? styles.mobileMenuOpen : ''}`}
      role="banner"
    >
      <Container>
        <nav className={styles.nav} role="navigation" aria-label="Main navigation">
          {/* Left Side - Language Switcher + Home + Solutions */}
          <div className={styles.navLeft}>
            {/* Language Switcher - helemaal links op desktop */}
            <LanguageSwitcher />
            
            <ul className={styles.navList}>
              {/* Left Navigation Items (Home) */}
              {leftNavigation?.map((item) => (
                <li key={item.href}>
                  <Link
                    href={getLocalePath(locale, item.href)}
                    className={styles.navLink}
                    aria-current={pathname === getLocalePath(locale, item.href) ? 'page' : undefined}
                    prefetch={true}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}

              {/* Solutions Dropdown */}
              {solutions && solutions.length > 0 && solutionsLabel && (
                <li 
                  className={styles.dropdownContainer} 
                  ref={dropdownRef}
                  onMouseEnter={() => handleMouseEnter('solutions')}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`${styles.navLink} ${styles.dropdownTrigger} ${isDropdownOpen ? styles.active : ''}`}
                    aria-expanded={isDropdownOpen}
                    aria-haspopup="true"
                    onClick={() => handleDropdownClick('solutions')}
                  >
                    <span className={styles.solutionsText}>{solutionsLabel}</span>
                    <svg
                      className={`${styles.dropdownArrow} ${isDropdownOpen ? styles.rotated : ''}`}
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* Dropdown Menu - Always in DOM for instant image availability */}
                  <div className={isDropdownOpen ? styles.dropdown : styles.dropdownHidden}>
                    <div className={styles.dropdownGrid}>
                      {solutions.map((solution, index) => (
                        <React.Fragment key={solution.href}>
                          {solution.dividerBefore && (
                            <div className={styles.dropdownDivider} />
                          )}
                          <Link
                            href={getLocalePath(locale, solution.href)}
                            className={styles.dropdownItem}
                            onMouseEnter={() => preloadPageImages(solution.href)}
                            prefetch={true}
                            tabIndex={isDropdownOpen ? 0 : -1}
                          >
                            <div className={styles.dropdownItemIcon}>
                              <Image
                                src={solution.icon}
                                alt=""
                                width={48}
                                height={48}
                                className={styles.dropdownIconImage}
                                quality={75}
                              />
                            </div>
                            <div className={styles.dropdownItemContent}>
                              <div className={styles.dropdownItemLabel}>{solution.label}</div>
                              <div className={styles.dropdownItemDescription}>{solution.description}</div>
                            </div>
                          </Link>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </li>
              )}

              {/* Integrations Dropdown */}
              {integrations && integrations.length > 0 && integrationsLabel && (
                <li 
                  className={styles.dropdownContainer} 
                  ref={integrationsDropdownRef}
                  onMouseEnter={() => handleMouseEnter('integrations')}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`${styles.navLink} ${styles.dropdownTrigger} ${isIntegrationsDropdownOpen ? styles.active : ''}`}
                    aria-expanded={isIntegrationsDropdownOpen}
                    aria-haspopup="true"
                    onClick={() => handleDropdownClick('integrations')}
                  >
                    <span className={styles.solutionsText}>{integrationsLabel}</span>
                    <svg
                      className={`${styles.dropdownArrow} ${isIntegrationsDropdownOpen ? styles.rotated : ''}`}
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* Integrations Dropdown Menu - Always in DOM for instant image availability */}
                  <div className={isIntegrationsDropdownOpen ? styles.dropdown : styles.dropdownHidden}>
                    <div className={styles.dropdownGrid}>
                      {integrations.map((integration) => (
                        <React.Fragment key={integration.href}>
                          {integration.dividerBefore && (
                            <div className={styles.dropdownDivider} />
                          )}
                          <Link
                            href={getLocalePath(locale, integration.href)}
                            className={styles.dropdownItem}
                            prefetch={true}
                            tabIndex={isIntegrationsDropdownOpen ? 0 : -1}
                          >
                            <div className={styles.dropdownItemIcon}>
                              <Image
                                src={integration.icon}
                                alt=""
                                width={48}
                                height={48}
                                className={styles.dropdownIconImage}
                                quality={75}
                              />
                            </div>
                            <div className={styles.dropdownItemContent}>
                              <div className={styles.dropdownItemLabel}>{integration.label}</div>
                              <div className={styles.dropdownItemDescription}>{integration.description}</div>
                            </div>
                          </Link>
                        </React.Fragment>
                      ))}
                    </div>
                    
                    {/* View All Integrations Link */}
                    {viewAllIntegrationsLink && (
                      <div className={styles.dropdownFooter}>
                        <Link
                          href={getLocalePath(locale, viewAllIntegrationsLink.href)}
                          className={styles.viewAllLink}
                          tabIndex={isIntegrationsDropdownOpen ? 0 : -1}
                          prefetch={true}
                        >
                          {viewAllIntegrationsLink.label}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                            />
                          </svg>
                        </Link>
                      </div>
                    )}
                  </div>
                </li>
              )}
            </ul>
          </div>

          {/* Center - Logo */}
          <div className={styles.navCenter}>
            <Link href={getLocalePath(locale, '/')} className={styles.logo} aria-label="Jamezz Home">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img 
                src="/images/logo-white.svg" 
                alt="Jamezz"
                width="62"
                height="40"
                className={styles.logoImage}
              />
            </Link>
          </div>

          {/* Right Side - Branches + Integrations + Actions */}
          <div className={styles.navRight}>
            <ul className={styles.navList}>
              {/* Branches Dropdown */}
              {branches && branches.length > 0 && branchesLabel && (
                <li 
                  className={styles.dropdownContainer} 
                  ref={branchesDropdownRef}
                  onMouseEnter={() => handleMouseEnter('branches')}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`${styles.navLink} ${styles.dropdownTrigger} ${isBranchesDropdownOpen ? styles.active : ''}`}
                    aria-expanded={isBranchesDropdownOpen}
                    aria-haspopup="true"
                    onClick={() => handleDropdownClick('branches')}
                  >
                    <span className={styles.solutionsText}>{branchesLabel}</span>
                    <svg
                      className={`${styles.dropdownArrow} ${isBranchesDropdownOpen ? styles.rotated : ''}`}
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* Branches Dropdown Menu - Always in DOM for instant image availability */}
                  <div className={isBranchesDropdownOpen ? styles.dropdown : styles.dropdownHidden}>
                    <div className={styles.dropdownGrid}>
                      {branches.map((branch) => (
                        <Link
                          key={branch.href}
                          href={getLocalePath(locale, branch.href)}
                          className={styles.dropdownItem}
                          prefetch={true}
                          tabIndex={isBranchesDropdownOpen ? 0 : -1}
                        >
                          <div className={styles.dropdownItemIcon}>
                            <Image
                              src={branch.icon}
                              alt=""
                              width={48}
                              height={48}
                              className={styles.dropdownIconImage}
                              quality={75}
                            />
                          </div>
                          <div className={styles.dropdownItemContent}>
                            <div className={styles.dropdownItemLabel}>{branch.label}</div>
                            <div className={styles.dropdownItemDescription}>{branch.description}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    {viewAllIndustriesLink && (
                      <div className={styles.dropdownFooter}>
                        <Link
                          href={getLocalePath(locale, viewAllIndustriesLink.href)}
                          className={styles.viewAllLink}
                          tabIndex={isBranchesDropdownOpen ? 0 : -1}
                          prefetch={true}
                        >
                          {viewAllIndustriesLink.label}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                            />
                          </svg>
                        </Link>
                      </div>
                    )}
                  </div>
                </li>
              )}

              {/* Reviews Link */}
              {reviewsLabel && reviewsHref && (
                <li>
                  <Link
                    href={getLocalePath(locale, reviewsHref)}
                    className={styles.navLink}
                    aria-current={pathname === getLocalePath(locale, reviewsHref) ? 'page' : undefined}
                    prefetch={true}
                  >
                    {reviewsLabel}
                  </Link>
                </li>
              )}

              {/* Blog Dropdown */}
              {blogs && blogs.length > 0 && blogsLabel && (
                <li 
                  className={styles.dropdownContainer} 
                  ref={blogsDropdownRef}
                  onMouseEnter={() => handleMouseEnter('blogs')}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`${styles.navLink} ${styles.dropdownTrigger} ${isBlogsDropdownOpen ? styles.active : ''}`}
                    aria-expanded={isBlogsDropdownOpen}
                    aria-haspopup="true"
                    onClick={() => handleDropdownClick('blogs')}
                  >
                    <span className={styles.solutionsText}>{blogsLabel}</span>
                    <svg
                      className={`${styles.dropdownArrow} ${isBlogsDropdownOpen ? styles.rotated : ''}`}
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* Blog Dropdown Menu - Always in DOM for instant image availability */}
                  <div className={isBlogsDropdownOpen ? styles.dropdown : styles.dropdownHidden}>
                    <div className={styles.dropdownGrid}>
                      {blogs.map((blog) => (
                        <Link
                          key={blog.href}
                          href={blog.href}
                          className={styles.dropdownItem}
                          prefetch={true}
                          tabIndex={isBlogsDropdownOpen ? 0 : -1}
                        >
                          <div className={styles.dropdownItemIcon}>
                            <Image
                              src={blog.icon}
                              alt=""
                              width={48}
                              height={48}
                              className={styles.dropdownIconImage}
                              quality={75}
                            />
                          </div>
                          <div className={styles.dropdownItemContent}>
                            <div className={styles.dropdownItemLabel}>{blog.label}</div>
                            <div className={styles.dropdownItemDescription}>{blog.description}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    
                    {/* View All Blogs Link */}
                    {viewAllBlogsLink && (
                      <div className={styles.dropdownFooter}>
                        <Link
                          href={viewAllBlogsLink.href}
                          className={styles.viewAllLink}
                          tabIndex={isBlogsDropdownOpen ? 0 : -1}
                          prefetch={true}
                        >
                          {viewAllBlogsLink.label}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                            />
                          </svg>
                        </Link>
                      </div>
                    )}
                  </div>
                </li>
              )}
            </ul>

            <div className={styles.actions}>
              {ctaLabel && ctaHref && (
                <Link href={getLocalePath(locale, ctaHref)} prefetch={true}>
                  <Button variant="primary" size="sm">
                    {ctaLabel}
                  </Button>
                </Link>
              )}

              <BackofficeIcon />

              {/* Mobile Menu Button */}
              <button
                className={styles.mobileMenuButton}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                }}
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span className={styles.hamburger}>
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>
            </div>
          </div>
        </nav>
      </Container>

      {/* Mobile Menu - Outside Container for fullscreen */}
      {isMobileMenuOpen && (
        <div className={styles.mobileMenu} role="dialog" aria-modal="true">
          <ul className={styles.mobileNavList}>
            {/* Home Link (Mobile) */}
            {homeLabel && homeHref && (
              <li>
                <Link
                  href={getLocalePath(locale, homeHref)}
                  className={styles.mobileNavLink}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {homeLabel}
                </Link>
              </li>
            )}

            {/* Mobile Solutions Accordion */}
            {solutions && solutions.length > 0 && solutionsLabel && (
              <li>
                <button
                  className={`${styles.mobileNavLink} ${styles.mobileDropdownTrigger} ${isMobileSolutionsOpen ? styles.active : ''}`}
                  onClick={() => setIsMobileSolutionsOpen(!isMobileSolutionsOpen)}
                  aria-expanded={isMobileSolutionsOpen}
                >
                  {solutionsLabel}
                  <svg
                    className={`${styles.mobileDropdownArrow} ${isMobileSolutionsOpen ? styles.rotated : ''}`}
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1.5L6 6.5L11 1.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <ul className={isMobileSolutionsOpen ? styles.mobileSubMenu : styles.mobileSubMenuHidden}>
                  {solutions.map((solution) => (
                    <li key={solution.href}>
                      <Link
                        href={getLocalePath(locale, solution.href)}
                        className={styles.mobileSubMenuLink}
                        tabIndex={isMobileSolutionsOpen ? 0 : -1}
                      >
                        <div className={styles.mobileSubMenuIcon}>
                          <Image
                            src={solution.icon}
                            alt=""
                            width={40}
                            height={40}
                            className={styles.mobileIconImage}
                            quality={75}
                          />
                        </div>
                        <div className={styles.mobileSubMenuContent}>
                          <div className={styles.mobileSubMenuLabel}>{solution.label}</div>
                          <div className={styles.mobileSubMenuDescription}>{solution.description}</div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            )}

            {/* Mobile Integrations Accordion */}
            {integrations && integrations.length > 0 && integrationsLabel && (
              <li>
                <button
                  className={`${styles.mobileNavLink} ${styles.mobileDropdownTrigger} ${isMobileIntegrationsOpen ? styles.active : ''}`}
                  onClick={() => setIsMobileIntegrationsOpen(!isMobileIntegrationsOpen)}
                  aria-expanded={isMobileIntegrationsOpen}
                >
                  {integrationsLabel}
                  <svg
                    className={`${styles.mobileDropdownArrow} ${isMobileIntegrationsOpen ? styles.rotated : ''}`}
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1.5L6 6.5L11 1.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <ul className={isMobileIntegrationsOpen ? styles.mobileSubMenu : styles.mobileSubMenuHidden}>
                  {integrations.map((integration) => (
                    <li key={integration.href}>
                      <Link
                        href={getLocalePath(locale, integration.href)}
                        className={styles.mobileSubMenuLink}
                        tabIndex={isMobileIntegrationsOpen ? 0 : -1}
                      >
                        <div className={styles.mobileSubMenuIcon}>
                          <Image
                            src={integration.icon}
                            alt=""
                            width={40}
                            height={40}
                            className={styles.mobileIconImage}
                            quality={75}
                          />
                        </div>
                        <div className={styles.mobileSubMenuContent}>
                          <div className={styles.mobileSubMenuLabel}>{integration.label}</div>
                          <div className={styles.mobileSubMenuDescription}>{integration.description}</div>
                        </div>
                      </Link>
                    </li>
                  ))}
                  {viewAllIntegrationsLink && (
                    <li>
                      <Link
                        href={getLocalePath(locale, viewAllIntegrationsLink.href)}
                        className={styles.mobileViewAllLink}
                        tabIndex={isMobileIntegrationsOpen ? 0 : -1}
                      >
                        {viewAllIntegrationsLink.label}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* Mobile Branches Accordion */}
            {branches && branches.length > 0 && branchesLabel && (
              <li>
                <button
                  className={`${styles.mobileNavLink} ${styles.mobileDropdownTrigger} ${isMobileBranchesOpen ? styles.active : ''}`}
                  onClick={() => setIsMobileBranchesOpen(!isMobileBranchesOpen)}
                  aria-expanded={isMobileBranchesOpen}
                >
                  {branchesLabel}
                  <svg
                    className={`${styles.mobileDropdownArrow} ${isMobileBranchesOpen ? styles.rotated : ''}`}
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1.5L6 6.5L11 1.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <ul className={isMobileBranchesOpen ? styles.mobileSubMenu : styles.mobileSubMenuHidden}>
                  {branches.map((branch) => (
                    <li key={branch.href}>
                      <Link
                        href={getLocalePath(locale, branch.href)}
                        className={styles.mobileSubMenuLink}
                        tabIndex={isMobileBranchesOpen ? 0 : -1}
                      >
                        <div className={styles.mobileSubMenuIcon}>
                          <Image
                            src={branch.icon}
                            alt=""
                            width={40}
                            height={40}
                            className={styles.mobileIconImage}
                            quality={75}
                          />
                        </div>
                        <div className={styles.mobileSubMenuContent}>
                          <div className={styles.mobileSubMenuLabel}>{branch.label}</div>
                          <div className={styles.mobileSubMenuDescription}>{branch.description}</div>
                        </div>
                      </Link>
                    </li>
                  ))}
                  {viewAllIndustriesLink && (
                    <li>
                      <Link
                        href={getLocalePath(locale, viewAllIndustriesLink.href)}
                        className={styles.mobileViewAllLink}
                        tabIndex={isMobileBranchesOpen ? 0 : -1}
                      >
                        {viewAllIndustriesLink.label}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* Reviews Link (Mobile) */}
            {reviewsLabel && reviewsHref && (
              <li>
                <Link
                  href={getLocalePath(locale, reviewsHref)}
                  className={styles.mobileNavLink}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {reviewsLabel}
                </Link>
              </li>
            )}

            {/* Mobile Blog Accordion */}
            {blogs && blogs.length > 0 && blogsLabel && (
              <li>
                <button
                  className={`${styles.mobileNavLink} ${styles.mobileDropdownTrigger} ${isMobileBlogsOpen ? styles.active : ''}`}
                  onClick={() => setIsMobileBlogsOpen(!isMobileBlogsOpen)}
                  aria-expanded={isMobileBlogsOpen}
                >
                  {blogsLabel}
                  <svg
                    className={`${styles.mobileDropdownArrow} ${isMobileBlogsOpen ? styles.rotated : ''}`}
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1.5L6 6.5L11 1.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <ul className={isMobileBlogsOpen ? styles.mobileSubMenu : styles.mobileSubMenuHidden}>
                  {blogs.map((blog) => (
                    <li key={blog.href}>
                      <Link
                        href={blog.href}
                        className={styles.mobileSubMenuLink}
                        tabIndex={isMobileBlogsOpen ? 0 : -1}
                      >
                        <div className={styles.mobileSubMenuIcon}>
                          <Image
                            src={blog.icon}
                            alt=""
                            width={40}
                            height={40}
                            className={styles.mobileIconImage}
                            quality={75}
                          />
                        </div>
                        <div className={styles.mobileSubMenuContent}>
                          <div className={styles.mobileSubMenuLabel}>{blog.label}</div>
                          <div className={styles.mobileSubMenuDescription}>{blog.description}</div>
                        </div>
                      </Link>
                    </li>
                  ))}
                  {viewAllBlogsLink && (
                    <li>
                      <Link
                        href={viewAllBlogsLink.href}
                        className={styles.mobileViewAllLink}
                        tabIndex={isMobileBlogsOpen ? 0 : -1}
                      >
                        {viewAllBlogsLink.label}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {ctaLabel && ctaHref && (
              <li className={styles.mobileCTA}>
                <Link
                  href={getLocalePath(locale, ctaHref)}
                  className={styles.ctaButton}
                >
                  {ctaLabel}
                </Link>
              </li>
            )}

            {/* Mobile Backoffice Link */}
            <li className={styles.mobileBackoffice}>
              <a
                href="https://manage.jamezz.com/manager/login"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className={styles.mobileBackofficeLink}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="8" r="3" />
                  <path d="M6 21c0-3.3 2.7-6 6-6s6 2.7 6 6" />
                  <path d="M18 12h4m0 0l-2-2m2 2l-2 2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Backoffice</span>
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
